import React, { useState, useRef, useEffect } from 'react';
import { MapPin, Search, Loader2, Building2, CheckCircle, AlertCircle } from 'lucide-react';
import usePlacesAutocomplete from 'use-places-autocomplete';
import api from '../../lib/api';
import toast from 'react-hot-toast';
import useGoogleMapsLoader from '../../hooks/useGoogleMapsLoader';
import { normalizeDistrictLabel } from '../../utils/districtDisplay';

export interface Districts {
  us_house?: string | null;
  state_senate?: string | null;
  state_house?: string | null;
  county?: string | null;
  municipality?: string | null;
}

export interface DistrictLookupResult {
  districts: Districts;
  formattedAddress: string;
  legislators?: any[];
}

interface GenericDistrictLookupProps {
  itemId?: string;
  onDistrictsFound: (districts: Districts, formattedAddress: string) => void;
  onLookupComplete?: (result: DistrictLookupResult) => void;
  initialAddress?: string;
  required?: boolean;
  primaryColor?: string;
  hideResults?: boolean;
}

const GenericDistrictLookup: React.FC<GenericDistrictLookupProps> = ({
  itemId,
  onDistrictsFound,
  onLookupComplete,
  initialAddress = '',
  required = false,
  primaryColor,
  hideResults = false,
}) => {
  const { isLoaded: mapsLoaded, loadError: mapsLoadError, loadGoogleMaps } = useGoogleMapsLoader();
  const [loading, setLoading] = useState(false);

  // Lazy load Google Maps when this component mounts
  useEffect(() => {
    if (!mapsLoaded && !mapsLoadError) {
      loadGoogleMaps();
    }
  }, [mapsLoaded, mapsLoadError, loadGoogleMaps]);
  
  const [districts, setDistricts] = useState<Districts | null>(null);
  const [formattedAddress, setFormattedAddress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Places autocomplete hook
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
    init,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'us' },
      types: ['address'],
    },
    debounce: 300,
    initOnMount: false,
  });

  // Initialize Places Autocomplete when Google Maps loads
  useEffect(() => {
    if (mapsLoaded && !ready) {
      init();
    }
  }, [mapsLoaded, ready, init]);

  // Set initial address if provided
  useEffect(() => {
    if (ready && initialAddress) {
      setValue(initialAddress, false);
    }
  }, [ready, initialAddress, setValue]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        clearSuggestions();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [clearSuggestions]);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    setShowSuggestions(true);
    // Clear previous results when typing
    if (districts) {
      setDistricts(null);
      setFormattedAddress(null);
    }
  };

  const handleSelect = async (description: string) => {
    setValue(description, false);
    clearSuggestions();
    setShowSuggestions(false);
    
    // Automatically trigger district lookup after selection
    await handleLookup(description);
  };

  const handleLookup = async (addressToLookup?: string) => {
    const lookupAddress = addressToLookup || value;
    
    if (!lookupAddress.trim()) {
      toast.error('Please enter an address');
      return;
    }

    setLoading(true);
    setError(null);
    setDistricts(null);

    try {
      const response = await api.post('/outreach-unified/district-lookup', {
        address: lookupAddress.trim(),
        item_id: itemId
      });

      const { districts: foundDistricts, formatted_address, legislators: foundLegislators } = response.data.data;
      setDistricts(foundDistricts);
      setFormattedAddress(formatted_address);
      onDistrictsFound(foundDistricts, formatted_address);
      if (onLookupComplete) {
        onLookupComplete({
          districts: foundDistricts,
          formattedAddress: formatted_address,
          legislators: foundLegislators,
        });
      }
      toast.success('Districts found!');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to lookup districts';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (data.length > 0 && showSuggestions) {
        // Select first suggestion
        handleSelect(data[0].description);
      } else {
        handleLookup();
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      clearSuggestions();
    }
  };

  const hasDistricts = districts && (
    districts.us_house || 
    districts.state_senate || 
    districts.state_house ||
    districts.county ||
    districts.municipality
  );

  // Show fallback if Google Maps API key is not configured
  const showFallbackInput = mapsLoadError || !import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  return (
    <div className="space-y-4">
      {/* Address Input */}
      <div ref={wrapperRef} className="relative">
        <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
          <MapPin className="w-4 h-4 inline mr-1" />
          District (Address + Legislative Districts)
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              value={value}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
              onFocus={() => data.length > 0 && setShowSuggestions(true)}
              disabled={loading || (!showFallbackInput && !ready)}
              className="input w-full"
              placeholder={
                showFallbackInput 
                  ? "Enter your full address (e.g., 123 Main St, Denver, CO 80202)"
                  : mapsLoaded 
                    ? "Start typing your address..."
                    : "Loading address lookup..."
              }
              required={required}
            />
            
            {/* Suggestions Dropdown */}
            {showSuggestions && status === 'OK' && data.length > 0 && (
              <ul className="absolute z-50 w-full mt-1 bg-white dark:bg-background-dark-secondary border border-border-light dark:border-border-dark rounded-lg shadow-lg max-h-60 overflow-auto">
                {data.map((suggestion) => {
                  const {
                    place_id,
                    structured_formatting: { main_text, secondary_text },
                  } = suggestion;

                  return (
                    <li
                      key={place_id}
                      onClick={() => handleSelect(suggestion.description)}
                      className="px-4 py-3 cursor-pointer hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary border-b border-border-light dark:border-border-dark last:border-b-0 transition-colors"
                    >
                      <div className="font-medium text-text-primary dark:text-text-dark-primary">
                        {main_text}
                      </div>
                      <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                        {secondary_text}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          
          <button
            type="button"
            onClick={() => handleLookup()}
            disabled={loading || !value.trim()}
            className={`px-4 py-2 text-white rounded-lg transition-colors flex items-center gap-2 ${primaryColor ? 'hover:opacity-90 disabled:opacity-50' : 'bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400'}`}
            style={primaryColor ? { backgroundColor: primaryColor } : undefined}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="hidden sm:inline">Looking up...</span>
              </>
            ) : (
              <>
                <Search className="w-4 h-4" />
                <span className="hidden sm:inline">Lookup</span>
              </>
            )}
          </button>
        </div>
        <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
          {mapsLoaded && !showFallbackInput
            ? "Start typing to see address suggestions, then select one to find your districts"
            : "Enter your address to find your congressional and state legislative districts"
          }
        </p>
      </div>

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-500" />
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      {/* Districts Display */}
      {hasDistricts && !hideResults && (
        <div className="border border-border-light dark:border-border-dark rounded-lg px-3 py-2">
          <div className="flex items-center gap-1.5 mb-1">
            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
            <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
              Districts Found
            </span>
            {formattedAddress && (
              <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary ml-1">
                — {formattedAddress}
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {districts.us_house && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary">
                <Building2 className="w-3 h-3 text-primary-600" />
                US House: <span className="font-medium">{normalizeDistrictLabel(districts.us_house)}</span>
              </span>
            )}
            {districts.state_senate && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary">
                <Building2 className="w-3 h-3 text-blue-600" />
                State Senate: <span className="font-medium">{normalizeDistrictLabel(districts.state_senate)}</span>
              </span>
            )}
            {districts.state_house && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary">
                <Building2 className="w-3 h-3 text-purple-600" />
                State House: <span className="font-medium">{normalizeDistrictLabel(districts.state_house)}</span>
              </span>
            )}
            {districts.county && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary">
                <Building2 className="w-3 h-3 text-amber-600" />
                County: <span className="font-medium">{districts.county}</span>
              </span>
            )}

            {districts.municipality && (
              <div className="flex items-center gap-2 p-2 bg-white dark:bg-background-dark-secondary rounded border border-border-light dark:border-border-dark">
                <Building2 className="w-4 h-4 text-green-600" />
                <div>
                  <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary">Municipality</div>
                  <div className="font-medium text-text-primary dark:text-text-dark-primary">
                    {districts.municipality}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GenericDistrictLookup;

